/*
  Local fork of VuexOk Lib:
  https://github.com/spb-web/vuexok/blob/a23abbf412b5fa0c28d58b787a69d9625a040070/src/vuexok.ts

  Includes the following fixes:
  - Fixes dynamic submodule registration (e.g. activity/typing)
  - Fixes mutations from a parent module that change state on a submodule, example:
    https://github.com/hwtears/KWT/blob/666639c29/src/store/activity/mutations.ts#L66
 */
import mitt from 'mitt';
import get from 'lodash/get';
export const modules = new Map();
const helperReduce = (payload, getter) => {
    if (payload) {
        return new Proxy(payload, {
            get(target, name) {
                if (Object.prototype.hasOwnProperty.call(target, name)) {
                    return getter(name, payload[name]);
                }
            }
        });
    }
};
const getStore = (module) => {
    const { $store } = module;
    if (!$store) {
        throw new Error(`Module ${module.path} not registered. Use module.register(store)\nsee https://spb-web.github.io/vuexok/MigrateTo1.x.x.html`);
    }
    return $store;
};
const initSubModules = (module, store) => {
    const { modules } = module;
    if (modules) {
        Object.values(modules).forEach(subModule => {
            subModule.$store = store;
            subModule.$events.emit('registered');
            initSubModules(subModule, store);
        });
    }
};
/**
 * @example
 * const module = createModule('path', {
 *   namespaced: true,
 *   state: () => ({
 *     count: 1,
 *   }),
 *   mutations: {
 *     increment(state) {
 *       state.count++
 *     }
 *   },
 * })
 *
 * module.once('registered', () => {
 *   setTimeout(() => {
 *     module.mutations.increment()
 *   })
 * })
 *
 * module.register(store)
 *
 * @param path
 * @param moduleRaw
 */
export const createModule = (path, moduleRaw) => {
    var _a;
    const { namespaced } = moduleRaw;
    const pathPrefix = namespaced ? path + '/' : '';
    const mutationsTypes = Object.keys((_a = moduleRaw.mutations) !== null && _a !== void 0 ? _a : {}).map(mutationType => `${pathPrefix}${mutationType}`);
    const module = {
        $events: mitt(),
        $store: undefined,
        state: new Proxy({}, {
            get(target, name) {
                return get(getStore(module).state, `${path.replaceAll('/', '.')}.${String(name)}`);
                // return getStore(module).state[path][name];
            }
        }),
        actions: helperReduce(moduleRaw.actions, key => {
            return (payload) => getStore(module).dispatch(`${pathPrefix}${key}`, payload);
        }),
        mutations: helperReduce(moduleRaw.mutations, key => {
            return (payload) => getStore(module).commit(`${pathPrefix}${key}`, payload);
        }),
        getters: helperReduce(moduleRaw.getters, key => {
            return getStore(module).getters[`${pathPrefix}${key}`];
        }),
        modules: helperReduce(moduleRaw.modules, (key, submodule) => {
            console.log('creating submodule', `${pathPrefix}${key}`);
            return createModule(`${pathPrefix}${key}`, submodule);
        }),
        watch: (getter, callback, options) => {
            return getStore(module).watch(() => getter(module.state, module.getters), callback, options);
        },
        subscribe(handle) {
            return getStore(module).subscribe((mutationPayload, state) => {
                if (mutationsTypes.includes(mutationPayload.type)) {
                    const payload = {
                        type: namespaced ? mutationPayload.type.substr(pathPrefix.length) : mutationPayload,
                        payload: mutationPayload.payload
                    };
                    handle(payload, state);
                }
            });
        },
        hasModule() {
            return !!module.$store && module.$store.hasModule(path);
        },
        unregister() {
            getStore(module).unregisterModule(path);
            module.$store = undefined;
            this.$events.emit('unregistered');
        },
        register(store, moduleOptions, throwErrorIfRegistered = false) {
            if (this.hasModule()) {
                if (throwErrorIfRegistered) {
                    throw new Error(`Module "${this.path}" already registered`);
                }
            }
            else {
                module.$store = store;
                console.log('registering module ', path);
                if (path.includes('/')) {
                    store.registerModule(path.split('/'), moduleRaw, moduleOptions);
                }
                else {
                    store.registerModule(path, moduleRaw, moduleOptions);
                }
                initSubModules(this, store);
                this.$events.emit('registered');
            }
        },
        on(type, handler) {
            this.$events.on(type, handler);
        },
        once(type, handler) {
            const offHandler = () => {
                this.$events.off(type, handler);
                this.$events.off(type, offHandler);
            };
            this.$events.on(type, handler);
            this.$events.on(type, offHandler);
        },
        off(type, handler) {
            this.$events.off(type, handler);
        },
        path
    };
    modules.set(path, module);
    return module;
};
